import { createContext, useState, useEffect, type ReactNode } from "react";

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'tv';
export type InputMethod = 'touch' | 'mouse' | 'keyboard' | 'remote';
export type Platform = 'ios' | 'android' | 'tvos' | 'androidtv' | 'webos' | 'tizen' | 'windows' | 'macos' | 'linux' | 'unknown';

interface DeviceContextValue {
  deviceType: DeviceType;
  inputMethod: InputMethod;
  platform: Platform;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTV: boolean;
  isTouchDevice: boolean;
  viewport: {
    width: number;
    height: number;
  };
}

export const DeviceContext = createContext<DeviceContextValue | undefined>(undefined);

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1280,
  desktop: 1920,
};

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  
  // TV platforms
  if (/webos|netcast/i.test(ua)) return 'webos';
  if (/tizen/i.test(ua)) return 'tizen';
  if (/tv|apple ?tv/i.test(ua)) return 'tvos';
  if (/android.*tv|aftm|aftb|afts|adt-/i.test(ua)) return 'androidtv';
  
  // Mobile platforms
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  
  // Desktop platforms
  if (/mac os x/i.test(ua)) return 'macos';
  if (/windows/i.test(ua)) return 'windows';
  if (/linux/i.test(ua)) return 'linux';
  
  return 'unknown';
}

function detectDeviceType(width: number, platform: Platform): DeviceType {
  // TV platforms always return TV regardless of size
  if (['tvos', 'androidtv', 'webos', 'tizen'].includes(platform)) {
    return 'tv';
  }
  
  // Check for TV via screen size
  if (width >= BREAKPOINTS.desktop) {
    return 'tv';
  }
  
  if (width >= BREAKPOINTS.tablet) {
    return 'desktop';
  }
  
  if (width >= BREAKPOINTS.mobile) {
    return 'tablet';
  }
  
  return 'mobile';
}

function detectInputMethod(platform: Platform, deviceType: DeviceType): InputMethod {
  // TV platforms always use remote
  if (['tvos', 'androidtv', 'webos', 'tizen'].includes(platform) || deviceType === 'tv') {
    return 'remote';
  }
  
  // Mobile devices use touch
  if (platform === 'ios' || platform === 'android') {
    return 'touch';
  }
  
  // Desktop defaults to mouse (will be updated by event listeners)
  return 'mouse';
}

interface DeviceProviderProps {
  children: ReactNode;
}

export function DeviceProvider({ children }: DeviceProviderProps) {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  const platform = detectPlatform();
  const deviceType = detectDeviceType(viewport.width, platform);
  const [inputMethod, setInputMethod] = useState<InputMethod>(
    detectInputMethod(platform, deviceType)
  );
  
  // Track viewport changes
  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Track input method changes
  useEffect(() => {
    // Don't change input method for TV/remote devices
    if (inputMethod === 'remote') return;
    
    let mouseMoveTimeout: NodeJS.Timeout;
    
    const handleMouseMove = () => {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = setTimeout(() => {
        setInputMethod('mouse');
      }, 100);
    };
    
    const handleTouchStart = () => {
      setInputMethod('touch');
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key.startsWith('Arrow')) {
        setInputMethod('keyboard');
        document.body.classList.add('keyboard-navigation');
      }
    };
    
    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-navigation');
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
      clearTimeout(mouseMoveTimeout);
    };
  }, [inputMethod]);
  
  const value: DeviceContextValue = {
    deviceType,
    inputMethod,
    platform,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isTV: deviceType === 'tv',
    isTouchDevice: inputMethod === 'touch' || inputMethod === 'remote',
    viewport,
  };
  
  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
}
